import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { FadeUp, Kicker, MaskedLine } from "@/components/Reveal";

export default function WorkDetail() {
  const { id } = useParams();
  const [work, setWork] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/works/${id}`)
      .then((r) => { setWork(r.data); document.title = `${r.data.title} — Luca Mannu Giardini`; })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <main className="mx-auto max-w-3xl px-6 pb-32 pt-48 text-center" data-testid="work-not-found">
        <h1 className="font-serif text-4xl text-forest">Lavoro non trovato</h1>
        <Link to="/lavori" className="link-underline mt-6 inline-block font-accent text-xs uppercase tracking-[0.2em] text-terra">Tutti i lavori</Link>
      </main>
    );

  if (!work)
    return <main className="mx-auto max-w-3xl px-6 pb-32 pt-48 text-sage" data-testid="work-loading">Caricamento…</main>;

  const hasBeforeAfter = work.before_image && work.after_image;
  const gallery = work.gallery || [];

  return (
    <main data-testid="work-detail-page">
      <header className="relative flex min-h-[80vh] items-end overflow-hidden bg-forest">
        <img src={work.image} alt={work.title} className="absolute inset-0 h-full w-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-forest via-forest/30 to-transparent" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-16 lg:px-10">
          <FadeUp>
            <Link to="/lavori" className="flex w-fit items-center gap-2 font-accent text-xs uppercase tracking-[0.2em] text-bone/70 hover:text-bone" data-testid="work-back-link">
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Tutti i lavori
            </Link>
            <Kicker className="mt-8 flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5" strokeWidth={1.5} /> {work.location} · {work.year}
            </Kicker>
            <h1 className="mt-4 font-serif text-5xl tracking-tight text-bone md:text-6xl" data-testid="work-title">
              <MaskedLine delay={0.1}>{work.title}</MaskedLine>
            </h1>
          </FadeUp>
        </div>
      </header>

      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
        <div className="grid gap-12 md:grid-cols-12">
          <FadeUp className="md:col-span-4">
            <Kicker>Il progetto</Kicker>
            <p className="mt-6 font-serif text-2xl italic leading-relaxed text-moss">{work.description}</p>
          </FadeUp>
          {work.story && (
            <FadeUp delay={0.15} className="md:col-span-7 md:col-start-6">
              <div className="flex flex-col gap-6">
                {work.story.split(/\n+/).filter(Boolean).map((p, i) => (
                  <p key={i} className="text-lg leading-relaxed text-forest/80">{p}</p>
                ))}
              </div>
            </FadeUp>
          )}
        </div>
      </section>

      {hasBeforeAfter && (
        <section className="bg-sand/60 py-20 lg:py-28" data-testid="work-before-after">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <Kicker>La trasformazione</Kicker>
            <h2 className="mt-4 font-serif text-4xl tracking-tight text-forest md:text-5xl">
              <MaskedLine delay={0.1}>Prima</MaskedLine>
              <MaskedLine delay={0.22} className="italic text-moss">e dopo.</MaskedLine>
            </h2>
            <div className="mt-14 grid gap-8 md:grid-cols-2">
              <FadeUp>
                <div className="arch-frame overflow-hidden bg-sand">
                  <img src={work.before_image} alt={`${work.title} — prima`} loading="lazy" className="aspect-[4/5] w-full object-cover" data-testid="work-before-image" />
                </div>
                <p className="mt-4 font-accent text-xs uppercase tracking-[0.25em] text-sage">Prima</p>
              </FadeUp>
              <FadeUp delay={0.15}>
                <div className="arch-frame overflow-hidden bg-sand">
                  <img src={work.after_image} alt={`${work.title} — dopo`} loading="lazy" className="aspect-[4/5] w-full object-cover" data-testid="work-after-image" />
                </div>
                <p className="mt-4 font-accent text-xs uppercase tracking-[0.25em] text-terra">Dopo</p>
              </FadeUp>
            </div>
          </div>
        </section>
      )}

      {gallery.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28" data-testid="work-gallery">
          <Kicker>Galleria</Kicker>
          <h2 className="mt-4 font-serif text-4xl tracking-tight text-forest md:text-5xl">
            <MaskedLine delay={0.1}>Dettagli del giardino.</MaskedLine>
          </h2>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {gallery.map((g, i) => (
              <FadeUp key={i} delay={(i % 3) * 0.1}>
                <div className={`overflow-hidden bg-sand ${i % 2 ? "rounded-sm" : "arch-frame"}`}>
                  <img src={g} alt={`${work.title} — dettaglio ${i + 1}`} loading="lazy" className={`w-full object-cover transition-transform duration-700 hover:scale-105 ${i % 2 ? "aspect-[16/11]" : "aspect-[3/4]"}`} data-testid={`work-gallery-image-${i}`} />
                </div>
              </FadeUp>
            ))}
          </div>
        </section>
      )}

      <section className="bg-forest py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <h2 className="max-w-2xl font-serif text-4xl leading-tight tracking-tight text-bone md:text-5xl">
              Vuoi una trasformazione <span className="italic text-terra">così?</span>
            </h2>
            <Link to="/contatti" data-testid="work-quote-cta" className="mt-10 inline-flex items-center gap-2 rounded-full bg-bone px-10 py-4 font-accent text-sm uppercase tracking-[0.2em] text-forest transition-transform duration-300 hover:scale-105 active:scale-95">
              Richiedi un preventivo <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
